/**
 * React Query hooks for the Prime booking pages and activity trail.
 *
 * The booking context key carries the mode, the referral codes and the signed-in
 * user's id, so a change of any of them makes the old context irrelevant: the page
 * drops back to loading and the old calendar unmounts instead of showing another
 * person's attribution. Each query forwards React Query's `signal` to `fetch`.
 */

import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/features/auth';

import {
  PrimeBookingApiError,
  fetchActivity,
  fetchGuestContext,
  fetchMemberContext,
  fetchPrimeBookingAccess,
  recordBookingEvent,
} from '../services/prime-booking-service';
import type { ActivityFilters, BookingMode, BrowserEventInput } from '../types';

const KEY = 'prime-booking';

/** 4xx answers are final (no referral, no SMD, signed out); only retry server trouble. */
function retryServerErrors(failureCount: number, error: Error) {
  if (error instanceof PrimeBookingApiError && error.status < 500) return false;
  return failureCount < 2;
}

export function useBookingContext(mode: BookingMode, agentCodes: string[] = []) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [KEY, 'context', mode, agentCodes, user?.id ?? null],
    queryFn: ({ signal }) =>
      mode === 'member' ? fetchMemberContext(signal) : fetchGuestContext(agentCodes, signal),
    retry: retryServerErrors,
    // Every fetch writes a server trail row: refetch on each visit (a page view, and the
    // SMD is resolved afresh), but not behind the user's back on focus or reconnect.
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/**
 * Returns `track(input)` for browser trail events. The referral is sent only when
 * the link carried exactly one; the backend re-resolves agent and SMD either way.
 */
export function useBookingEventRecorder(mode: BookingMode, agentCodes: string[] = []) {
  const agent = mode === 'guest' && agentCodes.length === 1 ? agentCodes[0] : undefined;
  return useCallback(
    (input: BrowserEventInput) => {
      void recordBookingEvent({ ...input, mode, ...(agent ? { agent } : {}) });
    },
    [mode, agent],
  );
}

/** Whether the current user may view the activity trail (`prime_booking:read`). */
export function usePrimeBookingAccess() {
  return useQuery({
    queryKey: [KEY, 'my-access'],
    queryFn: ({ signal }) => fetchPrimeBookingAccess(signal),
    staleTime: 5 * 60 * 1000,
  });
}

export function usePrimeBookingActivity(filters: ActivityFilters, page: number) {
  return useQuery({
    queryKey: [KEY, 'activity', filters, page],
    queryFn: ({ signal }) => fetchActivity(filters, page, signal),
    retry: retryServerErrors,
  });
}
